import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import { type Task } from 'ember-concurrency';
import type Flow from 'garaje/models/flow';
import type SignInField from 'garaje/models/sign-in-field';
import type SensitiveDataCategoriesService from 'garaje/services/sensitive-data-categories';
import type { SensitiveItem } from 'garaje/types/sensitive-item';

interface SignInFieldsFieldOptionsModalSensitiveFieldsComponentSignature {
  Args: {
    signInFields?: Array<SignInField>;
    flow?: Flow;
    saveFieldsTask?: Task<void, []>;
    isDisabled?: boolean;
    isPristine?: boolean;
    onRequestSave?: () => void;
    onDropdownToggle?: (isOpen: boolean) => void;
  };
}

export default class SignInFieldsFieldOptionsModalSensitiveFieldsComponent extends Component<SignInFieldsFieldOptionsModalSensitiveFieldsComponentSignature> {
  @service('sensitive-data-categories') declare sensitiveDataCategories: SensitiveDataCategoriesService;

  optionsName = 'sensitive-fields';

  @tracked searchQuery = '';

  private get flowId(): string | undefined {
    return this.args.flow?.id;
  }

  get allSensitiveItems(): Array<SensitiveItem> {
    const fields: Array<SensitiveItem> = (this.args.signInFields ?? []).map((field) => ({
      type: 'field' as const,
      id: field.id,
      displayName: field.name ?? '',
      model: field,
    }));

    // Categories come from the backend (available_categories), not a hardcoded
    // list, so a new category needs only an envoy-web deploy to appear here.
    const categories: Array<SensitiveItem> = this.sensitiveDataCategories
      .availableCategories(this.flowId)
      .map((category) => ({
        type: 'category' as const,
        id: `category-${category}`,
        displayName: category,
        dataCategory: category,
      }));

    return [...fields, ...categories];
  }

  // The full name field cannot be marked sensitive (VIS-17356). Categories and
  // all other fields are selectable. Used to gate selection everywhere.
  isItemSelectable = (item: SensitiveItem): boolean => item.type !== 'field' || !item.model.isGuestName;

  get selectableItems(): Array<SensitiveItem> {
    return this.allSensitiveItems.filter((item) => this.isItemSelectable(item));
  }

  get filteredItems(): Array<SensitiveItem> {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.allSensitiveItems;
    return this.allSensitiveItems.filter((item) => item.displayName.toLowerCase().includes(query));
  }

  get selectedItems(): Array<SensitiveItem> {
    return this.allSensitiveItems.filter((item) => this.isItemSensitive(item));
  }

  get hasSelectedItems(): boolean {
    return this.selectedItems.length > 0;
  }

  isItemSensitive = (item: SensitiveItem): boolean => {
    if (item.type === 'field') {
      return item.model.sensitive;
    }
    return !!this.flowId && this.sensitiveDataCategories.isSelected(this.flowId, item.dataCategory);
  };

  get isSelectAllDisabled(): boolean {
    return this.selectableItems.length === 0 || this.selectableItems.every((item) => this.isItemSensitive(item));
  }

  get isDeselectAllDisabled(): boolean {
    return this.selectedItems.length === 0;
  }

  @action
  handleSearch(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  private setItemSensitive(item: SensitiveItem, value: boolean): void {
    if (!this.isItemSelectable(item)) return;
    if (item.type === 'field') {
      item.model.sensitive = value;
    } else if (this.flowId) {
      this.sensitiveDataCategories.setSensitive(this.flowId, item.dataCategory, value);
    }
  }

  @action
  toggleItem(item: SensitiveItem): void {
    this.setItemSensitive(item, !this.isItemSensitive(item));
  }

  @action
  removeItem(item: SensitiveItem): void {
    this.setItemSensitive(item, false);
  }

  @action
  selectAll(): void {
    this.allSensitiveItems.forEach((item) => this.setItemSensitive(item, true));
  }

  @action
  deselectAll(): void {
    this.allSensitiveItems.forEach((item) => this.setItemSensitive(item, false));
  }

  get isCTAVisible(): boolean {
    return !this.args.isDisabled && typeof this.args.onRequestSave === 'function';
  }

  get isCTADisabled(): boolean {
    return !!this.args.isDisabled || !!this.args.isPristine || !!this.args.saveFieldsTask?.isRunning;
  }

  @action
  handleRequestSave(): void {
    this.args.onRequestSave?.();
  }

  @action
  handleDropdownOpen(): void {
    this.args.onDropdownToggle?.(true);
  }

  @action
  handleDropdownClose(): void {
    this.args.onDropdownToggle?.(false);
    this.searchQuery = '';
  }

  willDestroy(): void {
    super.willDestroy();
    this.args.onDropdownToggle?.(false);
  }

  <template>
    <div class="px-8 overflow-none" data-test-field-options={{this.optionsName}}>
      <p class="text-sm text-carbon-60 mb-6 pr-12">
        Responses will only be visible to roles with the
        <span class="font-semibold">Access sensitive fields</span>
        permission.
      </p>

      <p class="text-sm text-carbon-50 font-bold mb-2">Add fields</p>
      <BasicDropdown
        @matchTriggerWidth={{true}}
        @renderInPlace={{false}}
        @destination="modal-overlays"
        @verticalPosition="auto"
        @disabled={{@isDisabled}}
        @onOpen={{this.handleDropdownOpen}}
        @onClose={{this.handleDropdownClose}}
        as |dropdown|
      >
        <dropdown.Trigger class="ember-power-select-trigger w-full" data-test-sensitive-fields-search>
          <span class="ember-power-select-placeholder">Search fields</span>
        </dropdown.Trigger>
        <dropdown.Content>
          <div class="ember-power-select-dropdown bg-white">
            <div class="py-2 px-2 bg-carbon-5">
              <input
                type="search"
                class="pageSearch rounded-lg border icon-search w-full"
                placeholder="Search fields"
                data-test-sensitive-fields-search-input
                value={{this.searchQuery}}
                {{on "input" this.handleSearch}}
              />
            </div>

            <div class="flex items-center justify-start px-3 py-2 text-sm border-b border-carbon-10">
              <button
                data-test-sensitive-fields-select-all
                type="button"
                class="btn--link !text-gem-50"
                disabled={{this.isSelectAllDisabled}}
                {{on "click" this.selectAll}}
              >
                Select all
              </button>
              <span class="mx-1 select-none text-carbon-50">|</span>
              <button
                data-test-sensitive-fields-deselect-all
                type="button"
                class="btn--link !text-gem-50"
                disabled={{this.isDeselectAllDisabled}}
                {{on "click" this.deselectAll}}
              >
                Deselect all
              </button>
            </div>

            <ul class="ember-power-select-options" style="max-height: 240px; overflow-y: auto;">
              {{#each this.filteredItems as |item|}}
                <li data-test-sensitive-fields-option={{item.id}} class="p-0" tabindex="-1">
                  <label
                    class="ember-power-select-option flex items-center space-x-2
                      {{unless (this.isItemSelectable item) 'cursor-not-allowed'}}"
                  >
                    <input
                      type="checkbox"
                      class={{unless
                        (this.isItemSelectable item)
                        "appearance-none h-3.5 w-3.5 rounded border border-carbon-20 bg-carbon-20 cursor-not-allowed"
                      }}
                      checked={{this.isItemSensitive item}}
                      disabled={{unless (this.isItemSelectable item) true}}
                      {{on "change" (fn this.toggleItem item)}}
                    />
                    {{#if (this.isItemSelectable item)}}
                      <span class="text-base {{if (this.isItemSensitive item) 'text-gem-60' 'text-carbon-70'}}">
                        {{item.displayName}}
                      </span>
                    {{else}}
                      <span class="text-base text-carbon-30 cursor-not-allowed">
                        {{item.displayName}}
                      </span>
                    {{/if}}
                  </label>
                </li>
              {{else}}
                <li class="text-center py-2 text-carbon-40">No results</li>
              {{/each}}
            </ul>
          </div>
        </dropdown.Content>
      </BasicDropdown>

      <p class="text-sm text-carbon-50 font-bold mt-6 mb-2">
        Marked as sensitive
        {{#if this.hasSelectedItems}}
          <span
            class="ml-1 inline-flex items-center justify-center rounded-full bg-carbon-10 text-carbon-60 text-xs px-1.5"
            data-test-sensitive-fields-count
          >{{this.selectedItems.length}}</span>
        {{/if}}
      </p>
      <div
        data-test-sensitive-fields-list
        class="border border-dashed border-carbon-40 rounded-md p-4 mb-6 divide-y divide-carbon-10"
      >
        {{#if this.hasSelectedItems}}
          {{#each this.selectedItems as |item|}}
            <div data-test-sensitive-field={{item.id}} class="flex items-center justify-between py-2">
              <span class="text-sm text-carbon-50">{{item.displayName}}</span>
              <button
                type="button"
                disabled={{@isDisabled}}
                aria-label="Remove {{item.displayName}} from sensitive fields"
                class="bg-transparent border-0 p-0 text-black text-xl leading-none
                  {{if @isDisabled 'cursor-not-allowed opacity-40' 'cursor-pointer'}}"
                {{on "click" (fn this.removeItem item)}}
                data-test-remove-sensitive-field={{item.id}}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          {{/each}}
        {{else}}
          <p data-test-empty-text class="text-center text-carbon-40 m-0">
            No sensitive fields yet
          </p>
        {{/if}}
      </div>

      {{#if this.isCTAVisible}}
        <button
          disabled={{this.isCTADisabled}}
          class="btn btn--small btn--brand float-right mb-8 {{if this.isCTADisabled 'disabled'}}"
          {{on "click" this.handleRequestSave}}
          data-test-save-options={{this.optionsName}}
        >
          Save
        </button>
      {{/if}}
    </div>
  </template>
}
